// components/ImageCropper.tsx
import { useState, useEffect } from "react";
import ReactCrop from "react-image-crop";
import { type Crop } from "react-image-crop";

import "react-image-crop/dist/ReactCrop.css";
import { canvasPreview, getCroppedBlob } from "./croputils";
import { useImageCropper } from "./useImageCropper";

interface ImageCropperProps {
  onCropComplete: (file: File) => void;
  aspectRatio?: number;
  minDimension?: number;
}

export default function ImageCropper({
  onCropComplete,
  aspectRatio = 1,
  minDimension = 150,
}: ImageCropperProps) {
  const {
    imgSrc,
    crop,
    setCrop,
    completedCrop,
    setCompletedCrop,
    error,
    imgRef,
    previewCanvasRef,
    onSelectFile,
    onImageLoad,
    reset,
  } = useImageCropper(aspectRatio);

  const [isLoading, setIsLoading] = useState(false);

  // Live preview effect
  useEffect(() => {
    if (
      completedCrop?.width &&
      completedCrop?.height &&
      imgRef.current &&
      previewCanvasRef.current
    ) {
      canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
    }
  }, [completedCrop]);

  const handleCrop = async () => {
    if (!imgRef.current || !completedCrop) {
      return;
    }

    setIsLoading(true);
    const croppedFile = await getCroppedBlob(
      imgRef.current,
      completedCrop,
      "cropped-image.jpeg",
    );

    if (croppedFile) {
      onCropComplete(croppedFile);
    }
    setIsLoading(false);
  };

  return (
    <section className="flex flex-col items-center gap-4 p-4">
      {!imgSrc ? (
        <label
          className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-400 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          htmlFor="image-upload"
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <svg
              className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" /* ... SVG icon ... */
            />
            <p className="mb-2 text-sm text-gray-600 dark:text-gray-300">
              <span className="font-semibold">Click to upload</span> or drag and
              drop
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">PNG, JPG, or WEBP</p>
          </div>
          <input
            accept="image/*"
            className="hidden"
            id="image-upload"
            type="file"
            onChange={onSelectFile}
          />
        </label>
      ) : (
        <div className="w-full flex flex-col md:flex-row items-center justify-center gap-8">
          {/* Left Side: Cropper */}
          <div className="flex flex-col items-center gap-2">
            <ReactCrop
              aspect={aspectRatio}
              circularCrop
              crop={crop}
              keepSelection
              minWidth={minDimension}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
            >
              <img
                ref={imgRef}
                alt="Upload"
                src={imgSrc}
                style={{ maxHeight: "60vh" }}
                onLoad={onImageLoad}
              />
            </ReactCrop>
            <button
              className="border border-gray-300 dark:border-gray-600 p-2 text-sm text-gray-600 dark:text-gray-300 rounded-lg hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              type="button"
              onClick={reset}
            >
              Change Image
            </button>
          </div>

          {/* Right Side: Preview & Actions */}
          <div className="flex flex-col items-center gap-4">
            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">Preview</h3>
            <canvas
              ref={previewCanvasRef}
              className="rounded-full border dark:border-gray-600"
              style={{
                objectFit: "contain",
                width: Math.min(minDimension, 150),
                height: Math.min(minDimension, 150),
              }}
            />
            <button
              className="w-full px-4 py-2 text-blue-700 text-[14px] font-medium hover:text-blue-500 disabled:bg-gray-400 transition-all cursor-pointer"
              disabled={!completedCrop || isLoading}
              type="button"
              onClick={handleCrop}
            >
              {isLoading ? "Cropping..." : "Crop & Upload Image"}
            </button>
          </div>
        </div>
      )}
      {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
    </section>
  );
}
